import { touchFloor } from './_touchTarget.js'

export const LoginForm = {
  flow: 'column',
  height: '100%',
  gap: 'C1',
  '@tabletS': {
    height: 'unset',
    align: 'stretch flex-start',
    width: '100%'
  },
  '@mobileL': {
    height: 'unset',
    align: 'stretch flex-start',
    width: '100%'
  },
  Flex: {
    gap: 'Z',
    flow: 'column',
    width: '100%',
    maxWidth: '100%',
    childProps: {
      width: '100%',
      '@tabletS': {
        maxWidth: '100%',
        align: 'stretch',
        width: '100%'
      },
      '@mobileL': {
        maxWidth: '100%',
        align: 'stretch',
        width: '100%'
      }
    },
    Title: null,
    Input: null,
    Identifier: {
      flow: 'column',
      align: 'stretch flex-start',
      gap: 'Y',
      width: '100%',
      '@mobileL': {
        width: '100%'
      },
      Title: {
        tag: 'span',
        text: '{{ auth.login.emailOrUsername | polyglot }}',
        padding: '0',
        width: '100%',
        minWidth: '0',
        lineHeight: '1.25',
        '@mobileL': {
          padding: '0',
          width: '100%',
          minWidth: '0',
          lineHeight: '1.25'
        }
      },
      Input: {
        // Mirror every keystroke into LoginWindow state via onInput.
        // We do NOT bind `value: (el, s) => s.identifier` — that
        // factory re-rendered the input on every state change and
        // raced with native typing, intermittently clearing the field.
        // Mirroring out via onInput keeps state in sync for the submit
        // handler to read, without ever writing input.value back from
        // state (so the browser owns the live input).
        // Playwright smoke harness binds via this exact string —
        // qa-automation/pages/signin.page.ts emailInput.
        'data-testid': 'content.LoginWindow.Flex.Identifier.Input',
        minHeight: 'controlH',
        width: '100%',
        theme: 'field',
        background: 'hover',
        type: 'text',
        name: 'username',
        ariaLabel: (el) => el.call('polyglot', 'auth.login.emailOrUsername'),
        required: true,
        autocapitalize: 'none',
        spellcheck: false,
        autocomplete: 'username',
        placeholder: (el) => el.call('polyglot', 'auth.login.emailOrUsernamePlaceholder'),
        round: 'radiusControl',
        fontSize: 'Z2',
        padding: '0 A',
        '@tabletS': {
          flex: '1 1 auto',
          minWidth: '0',
          width: '100%'
        },
        '@mobileL': {
          flex: '1 1 auto',
          minWidth: '0',
          width: '100%'
        },
        onInput: (ev, el, s) => {
          s.update({ identifier: el.node.value })
        }
      }
    },
    Password: {
      flow: 'column',
      align: 'stretch flex-start',
      gap: 'Y',
      width: '100%',
      '@mobileL': {
        width: '100%'
      },
      Title: {
        tag: 'span',
        text: '{{ auth.login.password | polyglot }}',
        padding: '0',
        width: '100%',
        minWidth: '0',
        lineHeight: '1.25',
        '@mobileL': {
          padding: '0',
          width: '100%',
          minWidth: '0',
          lineHeight: '1.25'
        }
      },
      Input: {
        // Same one-way mirror as Identifier — see note above. The
        // password mirror is doubly important: Chrome's password
        // manager clears the field at form submission time as a
        // privacy measure, so reading `input.value` inside the
        // submit handler returns '' even though the user typed.
        // Reading from `s.password` (captured at typing time) is
        // the only reliable path.
        // Playwright smoke harness binds via this exact string —
        // qa-automation/pages/signin.page.ts passwordInput.
        'data-testid': 'content.LoginWindow.Flex.Password.Input',
        minHeight: 'controlH',
        width: '100%',
        theme: 'field',
        background: 'hover',
        autocomplete: 'current-password',
        name: 'password',
        ariaLabel: (el) => el.call('polyglot', 'auth.login.password'),
        required: true,
        placeholder: (el) => el.call('polyglot', 'auth.login.password'),
        type: 'password',
        round: 'radiusControl',
        fontSize: 'Z2',
        padding: '0 A',
        '@tabletS': {
          flex: '1 1 auto',
          minWidth: '0',
          width: '100%'
        },
        '@mobileL': {
          flex: '1 1 auto',
          minWidth: '0',
          width: '100%'
        },
        onInput: (ev, el, s) => {
          s.update({ password: el.node.value })
        }
      }
    }
  },
  ModalFooter: {
    display: 'flex',
    flow: 'row',
    width: '100%',
    maxWidth: 'unset',
    align: 'center space-between',
    gap: 'Z',
    '@tabletS': {
      childProps: {
        width: '100%'
      }
    },
    '@mobileL': {
      flow: 'column',
      gap: 'Z',
      childProps: {
        width: '100%'
      }
    },
    CheckButton: {
      // Playwright smoke harness binds via this exact string —
      // qa-automation/pages/signin.page.ts submitButton.
      'data-testid': 'content.LoginWindow.Flex.ModalFooter.CheckButton',
      // Static label: the LoadingGifSection overlay (in LoginWindow) is the
      // loading affordance. A loading-conditional label ('Sign in' →
      // 'Signing in…') widened this fit-content button, and since LoginWindow
      // is boxSize:auto the whole card grew on submit (the layout shift).
      text: '{{ auth.login.submit | polyglot }}',
      disabled: (el, s) => !!s.loading,
      opacity: (el, s) => (s.loading ? '.7' : '1'),
      cursor: (el, s) => (s.loading ? 'wait' : 'pointer'),
      pointerEvents: (el, s) => (s.loading ? 'none' : 'auto'),
      type: 'submit',
      Icon: null,
      width: 'fit-content',
      minHeight: 'controlH',
      padding: '0 A',
      fontSize: 'Z2',
      fontWeight: '700',
      '@tabletS': {
        width: '100%',
        maxWidth: '100%'
      },
      '@mobileL': {
        width: '100%',
        maxWidth: '100%'
      }
    },
    Title: null,
    Input: null,
    DocsLink: {
      margin: '- - - auto',
      fontSize: 'Z2',
      fontWeight: '400',
      color: 'caption',
      href: '/reset-password',
      text: '{{ auth.login.forgot | polyglot }}',
      display: 'flex',
      align: 'center center',
      // A finger's target: the link is 20-23px tall (WebKit, iPhone / iPad
      // emulation of dev /signin, DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1) — the
      // 44pt floor (_touchTarget.js).
      '@coarse': { position: 'relative', ...touchFloor }
    }
  }
}
